import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import IconButtonExtended from '../../../../components/@extended/IconButton';
import Transitions from '../../../../components/@extended/Transitions';
import useAuth from '../../../../hooks/useAuth';
import { apiFetch, isApiAuthError } from '../../../../api/http';

// assets
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

type TicketStatus = 'idle' | 'sending' | 'success' | 'error';

export default function SupportTicket() {
  const MAX_TOTAL_ATTACHMENTS_BYTES = 4 * 1024 * 1024; // 4 MB
  const MB = 1024 * 1024;
  const KB = 1024;

  const theme = useTheme() as any;
  const downMD = useMediaQuery((t: any) => t.breakpoints.down('md'));
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { user } = useAuth();
  const { t } = useTranslation();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldAutoScrollRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [status, setStatus] = useState<TicketStatus>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);

  const supportEmail = useMemo(() => 'support@syn.digital', []);

  const escapeHtml = (input: string) =>
    input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const nl2br = (input: string) => escapeHtml(input).replace(/\n/g, '<br/>');

  const formatFileSize = (bytes: number) => {
    if (bytes < MB) return `${(bytes / KB).toFixed(0)} KB`;
    return `${(bytes / MB).toFixed(2)} MB`;
  };

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    const el = textAreaRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    shouldAutoScrollRef.current = false;
  }, [message]);

  useEffect(() => {
    if (!open) return;
    // Esperar al render del Popper para poder enfocar.
    const id = window.requestAnimationFrame(() => {
      textAreaRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
    setStatus('idle');
    setErrorText(null);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handlePickFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setAttachments((prev) => {
      const keyOf = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
      const existing = new Set(prev.map(keyOf));

      const prevTotalBytes = prev.reduce((acc, f) => acc + f.size, 0);
      let nextTotalBytes = prevTotalBytes;

      const next = [...prev];
      let addedCount = 0;
      let skippedBecauseLimit = 0;

      for (const f of files) {
        const key = keyOf(f);
        if (existing.has(key)) continue;

        if (nextTotalBytes + f.size > MAX_TOTAL_ATTACHMENTS_BYTES) {
          skippedBecauseLimit++;
          continue;
        }

        existing.add(key);
        next.push(f);
        nextTotalBytes += f.size;
        addedCount++;
      }

      if (skippedBecauseLimit > 0) {
        const maxMB = 4;
        setErrorText(
          t(addedCount > 0 ? 'supportTicket.errors.attachmentsTotalTooLargeSomeOmitted' : 'supportTicket.errors.attachmentsTotalTooLarge', {
            maxMB
          })
        );
      } else {
        // Si no hubo problema con el límite, no ensuciamos la UI con errores.
        setErrorText(null);
      }

      return next;
    });

    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    let body = message.trim();
    if (!body) return;

    setStatus('sending');
    setErrorText(null);
    try {
      const userEmail = user?.email || '';
      const to = [userEmail, supportEmail].filter(Boolean).join(',');
      const subject = `[Syn Digital] ${t('supportTicket.subject')}`;

      const username = (user as any)?.name || user?.email || '';
      const email = user?.email || '';
      const company = (user as any)?.companyName || '';

      const safeUserMessageHtml = nl2br(body);
      const safeUsername = escapeHtml(String(username));
      const safeEmail = escapeHtml(String(email));
      const safeCompany = escapeHtml(String(company));

      body = `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5;">
              <p>${t('supportTicket.email.greeting', { name: safeUsername })}</p>
              <p>${t('supportTicket.email.received')}</p>
              <p>${t('supportTicket.email.reviewing')}</p>
              <hr/>
              <p><strong>${t('supportTicket.email.userInformationTitle')}</strong></p>
              <ul>
                <li><strong>${t('supportTicket.email.fields.name')}:</strong> ${safeUsername}</li>
                <li><strong>${t('supportTicket.email.fields.email')}:</strong> ${safeEmail}</li>
                <li><strong>${t('supportTicket.email.fields.company')}:</strong> ${safeCompany}</li>
              </ul>
              <hr/>
              <p><strong>${t('supportTicket.email.ticketDescriptionTitle')}</strong></p>
              <p>${safeUserMessageHtml}</p>
            </div>`.trim();

      const form = new FormData();
      form.append('to', to);
      form.append('subject', subject);
      form.append('body', body);
      for (const file of attachments) form.append('files', file, file.name);

      const response = await apiFetch(`${import.meta.env.VITE_APP_API_URL}/email/send`, {
        method: 'POST',
        body: form
      });

      if (!response.ok) {
        throw new Error(t('supportTicket.errors.sendFailedWithStatus', { status: response.status, statusText: response.statusText }));
      }

      setStatus('success');
      setMessage('');
      setAttachments([]);
    } catch (err: any) {
      if (isApiAuthError(err)) {
        setStatus('idle');
        return;
      }
      setStatus('error');
      setErrorText(err?.message || t('supportTicket.errors.sendFailed'));
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title={t('supportTicket.tooltip')}>
        <IconButtonExtended
          size="xlarge"
          color="secondary"
          variant="light"
          sx={(t: any) => ({
            p: 0.2,
            pl: 1.5,
            pr: 1.5,
            height: 44,
            color: 'primary.main',
            bgcolor: open ? alpha((t as any).palette.primary.light, 0.15) : 'transparent',
            borderRadius: 1.5,
            transition: 'background-color 150ms ease, color 150ms ease',
            '&:hover': {
              bgcolor: alpha((t as any).palette.primary.light, 0.15),
              ...(t.applyStyles && t.applyStyles('dark', { bgcolor: alpha((t as any).palette.primary.dark, 0.2) }))
            },
            ...(t.applyStyles && t.applyStyles('dark', { bgcolor: open ? alpha((t as any).palette.primary.dark, 0.2) : 'transparent' }))
          })}
          ref={anchorRef}
          aria-haspopup="true"
          aria-label={t('supportTicket.aria.open')}
          aria-controls={open ? 'support-ticket-popper' : undefined}
          onClick={handleToggle}
        >
          <SupportAgentIcon sx={{ fontSize: 32 }} />
        </IconButtonExtended>
      </Tooltip>

      <Popper
        id="support-ticket-popper"
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{
          zIndex: (theme as any).zIndex?.modal || 1300
        }}
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }: any) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={{
                position: 'relative',
                boxShadow: theme.customShadows?.z1 || '0 2px 8px rgba(0,0,0,0.15)',
                width: { xs: 'calc(100vw - 16px)', md: '100%' },
                minWidth: { xs: 0, md: 550 },
                maxWidth: { xs: 'calc(100vw - 16px)', md: 550 },
                overflow: 'hidden',
                overscrollBehavior: 'contain',
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.primary.main, (theme as any).palette?.mode === 'dark' ? 0.25 : 0.15)}`,
                backdropFilter: reduceMotion ? 'none' : 'blur(10px)',
                WebkitBackdropFilter: reduceMotion ? 'none' : 'blur(10px)',
                transformStyle: 'preserve-3d',
                willChange: reduceMotion ? 'auto' : 'transform, box-shadow, opacity',
                animation: reduceMotion ? 'none' : open ? 'supportTicketPop 420ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
                '@keyframes supportTicketPop': {
                  '0%': {
                    opacity: 0,
                    transform: 'translate3d(0, -10px, 0) scale(0.98) rotateX(6deg)',
                    boxShadow: theme.customShadows?.z1 || '0 2px 8px rgba(0,0,0,0.15)'
                  },
                  '55%': {
                    opacity: 1,
                    transform: 'translate3d(0, 0, 0) scale(1.01) rotateX(0deg)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translate3d(0, 0, 0) scale(1) rotateX(0deg)',
                    boxShadow: theme.customShadows?.z1 || '0 2px 8px rgba(0,0,0,0.15)'
                  }
                },
                '&::before': {
                  display: 'none'
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', overscrollBehavior: 'contain' }}>
                  <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ mb: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: 'primary.dark',
                        letterSpacing: 0.2
                      }}
                    >
                      {t('supportTicket.title')}
                    </Typography>
                  </Stack>

                  <Typography variant="body1" sx={{ mb: 0.75, color: 'text.secondary' }}>
                    {t('supportTicket.intro')}
                  </Typography>

                  <Box
                    component="textarea"
                    ref={textAreaRef}
                    value={message}
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                    onChange={(e) => {
                      const target = e.currentTarget;
                      shouldAutoScrollRef.current = target.selectionStart === target.value.length;
                      setMessage(target.value);
                    }}
                    placeholder={t('supportTicket.placeholder')}
                    disabled={status === 'sending'}
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    sx={{
                      width: '100%',
                      height: 350,
                      minHeight: 350,
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${alpha((theme as any).palette.success.main, (theme as any).palette?.mode === 'dark' ? 0.35 : 0.25)}`,
                      bgcolor:
                        (theme as any).palette?.mode === 'dark'
                          ? alpha((theme as any).palette.success.main, 0.12)
                          : '#e8f5e9',
                      color: (theme as any).palette.text.primary,
                      fontFamily: 'inherit',
                      fontSize: 14,
                      lineHeight: 1.5,
                      outline: 'none',
                      resize: 'none',
                      overflowY: 'scroll',
                      overscrollBehavior: 'contain',
                      scrollbarGutter: 'stable',
                      scrollbarWidth: 'thin',
                      '&:focus': {
                        borderColor: (theme as any).palette.success.main,
                        boxShadow: `0 0 0 3px ${alpha((theme as any).palette.success.main, (theme as any).palette?.mode === 'dark' ? 0.22 : 0.18)}`
                      },
                      '&::placeholder': {
                        color: alpha((theme as any).palette.text.primary, 0.5)
                      },
                      '&::-webkit-scrollbar': {
                        width: 10
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha((theme as any).palette.text.primary, 0.25),
                        borderRadius: 8
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={handleFilesSelected}
                    aria-hidden="true"
                  />

                  {attachments.length > 0 && (
                    <Stack
                      sx={{
                        mt: 1,
                        maxHeight: { xs: 120, md: 160 },
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        scrollbarGutter: 'stable',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { width: 10 },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: alpha((theme as any).palette.text.primary, 0.2),
                          borderRadius: 8
                        },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
                      }}
                      spacing={0.5}
                    >
                      {attachments.map((f, idx) => (
                        <Stack
                          key={`${f.name}-${idx}`}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: (theme as any).palette.background.paper,
                            border: '1px solid',
                            borderColor: 'lightgray'
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pr: 1, minWidth: 0 }}>
                            <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                              {f.name}{' '}
                              <Box component="span" sx={{ color: 'text.secondary' }}>
                                ({formatFileSize(f.size)})
                              </Box>
                            </Typography>
                          </Stack>
                          <IconButton
                            size="small"
                            onClick={() => removeAttachment(idx)}
                            disabled={status === 'sending'}
                            aria-label={t('supportTicket.aria.removeAttachment', { fileName: f.name })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {errorText && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      {errorText}
                    </Typography>
                  )}

                  {status === 'success' && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ display: 'block', mt: 1, fontSize: 14, textAlign: 'left' }}
                    >
                      {t('supportTicket.success')}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="space-between" alignItems="center">
                    <Tooltip title={t('supportTicket.attach.tooltip')}>
                      <span>
                        <IconButton
                          onClick={handlePickFiles}
                          disabled={status === 'sending'}
                          aria-label={t('supportTicket.attach.aria')}
                          sx={(t) => ({
                            borderRadius: 1.5,
                            border: '1px solid lightgray'
                          })}
                        >
                          <AttachFileIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Button
                      variant="contained"
                      onClick={handleSend}
                      disabled={status === 'sending' || message.trim().length === 0}
                    >
                      {status === 'sending' ? t('supportTicket.actions.sending') : t('supportTicket.actions.send')}
                    </Button>
                  </Stack>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

